import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { API, COLORS } from '../constants';

const STATUS_COLORS = { paid: '#4da8ff', processing: '#ffb347', shipped: '#ff69b4', delivered: '#39d98a', cancelled: '#8a8a94', refunded: '#f87171' };
const STEPS = ['paid', 'processing', 'shipped', 'delivered'];

const TrackResult = ({ order, t }) => {
  const stepIndex = STEPS.indexOf(order.status);
  return (
    <div className="track-result" data-testid="track-result">
      <div className="track-head">
        <strong className="track-number">{order.order_number}</strong>
        <span className="track-status" style={{ '--c': STATUS_COLORS[order.status] }} data-testid="track-status">{t(`track.status.${order.status}`)}</span>
      </div>
      {stepIndex >= 0 && (
        <ol className="track-steps">
          {STEPS.map((s, i) => <li key={s} className={i <= stepIndex ? 'done' : ''} style={{ '--c': STATUS_COLORS[s] }}><i /><span>{t(`track.status.${s}`)}</span></li>)}
        </ol>
      )}
      <ul className="track-items">
        {order.items.map((it, i) => <li key={i}><i style={{ background: COLORS[it.line_color] || '#fff' }} />{it.name} <span>× {it.quantity}</span></li>)}
      </ul>
      {order.shipment?.tracking_number && (
        <p className="track-shipment" data-testid="track-shipment">
          {t('track.carrier')}: <strong>{order.shipment.carrier_name}</strong> · {order.shipment.tracking_number}
          {order.shipment.tracking_url && <a href={order.shipment.tracking_url} target="_blank" rel="noreferrer" data-testid="track-carrier-link"><ExternalLink size={14} /> {t('track.follow')}</a>}
        </p>
      )}
    </div>
  );
};

const TrackOrderModal = ({ onClose }) => {
  const { t } = useLanguage();
  const [number, setNumber] = useState('');
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setOrder(null);
    try {
      const { data } = await axios.get(`${API}/orders/track`, { params: { order_number: number.trim(), email: email.trim() } });
      setOrder(data);
    } catch {
      setError(t('track.not_found'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="track-order-modal">
      <motion.div className="track-modal" onClick={(e) => e.stopPropagation()} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <button onClick={onClose} className="share-close" aria-label={t('share.close')} data-testid="track-order-close">✕</button>
        <span className="eyebrow">{t('track.eyebrow')}</span>
        <h3 className="share-title text-gradient-brand">{t('track.title')}</h3>
        <p className="share-sub">{t('track.subtitle')}</p>
        <form className="track-form" onSubmit={submit}>
          <label className="share-field">
            <span>{t('track.order_number')}</span>
            <input value={number} onChange={(e) => setNumber(e.target.value.toUpperCase())} placeholder="BK-260911-51EB" required data-testid="track-number-input" />
          </label>
          <label className="share-field">
            <span>{t('track.email')}</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@email.it" required data-testid="track-email-input" />
          </label>
          <button type="submit" className="btn-pill btn-pill-primary" disabled={busy} data-testid="track-submit">{busy ? '…' : t('track.submit')}</button>
        </form>
        {error && <p className="track-error" role="alert" data-testid="track-error">{error}</p>}
        {order && <TrackResult order={order} t={t} />}
      </motion.div>
    </div>
  );
};

export default TrackOrderModal;
